/**
 * **Move Day 3** — `docs/visit-page.md` §15.3, Board 11 C (popup 470; on a
 * phone Board 16's sheet from the bottom).
 *
 * *V-1187 · and what depends on the date*: **From** (read-only) and **To**,
 * **Why** (required), the band about the new date, and **What moves with it**
 * — a row per dependency with what will happen, before anything moves (D17):
 * a vendor rental → **Call the vendor** (its dates change only when the
 * office marks the call done on the rental card); a PO pickup or delivery →
 * **Moves itself**; company equipment → **Moves itself**; the customer →
 * **Edit the text** (a prepared text, sent on **Move the day**; edited here,
 * or cleared to send nothing).
 *
 * `GET …/days/:n/move-preview?to=` is what the server will do;
 * `POST …/days/:n/move {to, reason, customerText?}` does it.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react'
import { WEATHER_TITLES, type MoveDependency, type MovePreview, type VisitRead } from '@rowted/shared'
import { cn } from '../lib/cn.js'
import { request } from '../lib/api.js'
import { usePhone } from '../lib/use-narrow.js'
import { DateCalendar } from '../components/date-field.js'
import { Glyph } from '../activity/glyphs.js'
import { useVisitWrite, visitPath } from './api.js'
import { VisitGlyph, type VisitGlyphName } from './glyphs.js'
import { VisitButton, VisitPopup, VisitTile, VISIT_LAYER } from './sheet.js'
import { TagChip } from './day-parts.js'
import { FIELD_BOX, FIELD_INPUT, FieldLabel } from './fields.js'
import { COMMON, MOVE_DAY } from './copy.js'
import { dayText, timeIn } from './format.js'

export function MoveDayPopup({ visitId, read, day, phone: forced, layer = VISIT_LAYER, onClose, onMoved }: {
  visitId: string
  read: VisitRead
  /** The day's index — *Move Day 3*. */
  day: number
  phone?: boolean
  layer?: number
  onClose: () => void
  /** After **Move the day** — the page has the fresh Overview already. */
  onMoved?: () => void
}) {
  const windowPhone = usePhone()
  const phone = forced ?? windowPhone
  const write = useVisitWrite(visitId)
  const tile = read.overview.days.days.find((one) => one.day.index === day)
  const [to, setTo] = useState('')
  const [why, setWhy] = useState('')
  const [preview, setPreview] = useState<MovePreview | null>(null)
  /** `undefined` sends the prepared text; a string, the text as edited; `null`, nothing (it was cleared). */
  const [text, setText] = useState<string | null | undefined>(undefined)
  const [editing, setEditing] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!to) { setPreview(null); return }
    let live = true
    request<MovePreview>(`${visitPath(visitId, `/days/${day}/move-preview`)}?to=${encodeURIComponent(to)}`)
      .then((body) => { if (live) { setPreview(body); setError(null) } })
      .catch((cause: unknown) => { if (live) setError(cause instanceof Error ? cause.message : String(cause)) })
    return () => { live = false }
  }, [visitId, day, to])

  const from = preview?.from ?? tile?.day.date ?? null
  const ready = Boolean(preview && to && why.trim()) && !busy

  async function move() {
    if (!ready) return
    setBusy(true)
    setError(null)
    try {
      await write('POST', `/days/${day}/move`, {
        to, reason: why.trim(), ...(text === undefined ? {} : { customerText: text }),
      }, { tab: 'overview' })
      onMoved?.()
      onClose()
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause))
    } finally {
      setBusy(false)
    }
  }

  const size = phone ? 'phone' : 'popup'
  return (
    <VisitPopup
      width={470}
      phone={phone}
      title={MOVE_DAY.title(String(day))}
      sub={MOVE_DAY.sub(read.reference)}
      mark={<VisitTile glyph="calendar" tone="amber" size={34} />}
      onClose={onClose}
      layer={layer}
      asDrawn="vp-11 C"
      footer={(
        <>
          <VisitButton size={size} onClick={onClose}>{MOVE_DAY.cancel}</VisitButton>
          <VisitButton size={size} tone="primary" glyph="calendar" disabled={!ready} onClick={() => void move()}>
            {MOVE_DAY.move}
          </VisitButton>
        </>
      )}
    >
      <div className="flex flex-col gap-2.5">
        <div className="grid grid-cols-2 gap-x-3.5 gap-y-3">
          <div className="min-w-0">
            <FieldLabel>{MOVE_DAY.from}</FieldLabel>
            <div className={cn(FIELD_BOX, 'text-text-faint')}>{from ? dayText(from, 'date') : ''}</div>
          </div>
          <div className="min-w-0">
            <FieldLabel>{MOVE_DAY.to}</FieldLabel>
            <DayBox value={to} label={MOVE_DAY.to} onChange={setTo} />
          </div>
        </div>

        <div className="min-w-0">
          <FieldLabel htmlFor="move-day-why">{MOVE_DAY.why}</FieldLabel>
          <div className={FIELD_BOX}>
            <input id="move-day-why" value={why} onChange={(event) => setWhy(event.target.value)} className={cn(FIELD_INPUT, 'tabular-nums')} />
          </div>
        </div>

        {preview && <DateBand preview={preview} />}

        {preview && preview.dependencies.length > 0 && (
          <div>
            <div className="mb-0.5 mt-1 text-[10.5px] font-semibold uppercase tracking-[.06em] text-text-faint">
              {MOVE_DAY.title2(String(preview.dependencies.length))}
            </div>
            {preview.dependencies.map((dependency) => (
              <DependencyRow
                key={dependencyKey(dependency)}
                dependency={dependency}
                tz={read.timeZone}
                text={text}
                editing={editing}
                onEdit={() => setEditing((was) => !was)}
                onText={setText}
              />
            ))}
          </div>
        )}

        {error && <p className="m-0 text-[12.5px] text-danger">{error}</p>}
      </div>
    </VisitPopup>
  )
}

function dependencyKey(dependency: MoveDependency): string {
  switch (dependency.kind) {
    case 'rental': return `rental-${dependency.rentalId}`
    case 'po': return `po-${dependency.po.id}`
    case 'equipment': return 'equipment'
    case 'customer': return `customer-${dependency.contact.id}`
  }
}

/**
 * **To** — a day, as the board prints it (*Mon Oct 26*), and the app's own
 * calendar under it (`DateCalendar`, the date field's month). Ringed in the
 * accent once a day is chosen: the date is what the rest of the popup reads.
 */
function DayBox({ value, label, onChange }: { value: string; label: string; onChange: (value: string) => void }) {
  const [open, setOpen] = useState(false)
  const box = useRef<HTMLButtonElement>(null)
  return (
    <>
      <button
        ref={box}
        type="button"
        aria-label={label}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen((was) => !was)}
        className={cn(FIELD_BOX, 'text-left',
          (value || open) && 'border-accent shadow-[0_0_0_3px_rgb(var(--color-accent-ring))]')}
      >
        <span className="min-w-0 flex-1 truncate">{value ? dayText(value, 'date') : ''}</span>
      </button>
      {open && (
        <DateCalendar
          anchor={box.current}
          label={label}
          selected={value}
          onPick={(iso) => { onChange(iso); setOpen(false) }}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  )
}

/**
 * The band about the new date — *Monday works. Hardscape 2 is free; sunny,
 * 59°. Nothing else is booked out of the yard that morning.* Drawn when the
 * day works as Board 11 C draws it; no board draws a day that does not, so
 * then there is no band (a DEVIATION REQUEST names the words it would need).
 */
function DateBand({ preview }: { preview: MovePreview }) {
  const { band } = preview
  if (!band.works || !band.crewFree || band.otherYardDepartures !== 0 || !band.crew || !band.forecast) return null
  const sky = WEATHER_TITLES[band.forecast.icon].toLowerCase()
  const temperature = band.forecast.high === null ? '' : `${band.forecast.high}°`
  return (
    <div className="flex items-start gap-[9px] rounded-[10px] border border-accent-line bg-accent-soft px-3 py-2.5
                    text-[12.5px] leading-[1.45] text-accent-dark">
      <Glyph name={band.forecast.icon} size={15} stroke={2} />
      <div>
        <b>{MOVE_DAY.works(dayText(preview.to, 'weekday'))}</b>{' '}
        {MOVE_DAY.worksSub(band.crew.name, sky, temperature)}
      </div>
    </div>
  )
}

const DEPENDENCY_GLYPHS: Record<MoveDependency['kind'], VisitGlyphName> = {
  rental: 'truck',
  po: 'pickup',
  equipment: 'tool',
  customer: 'bubble',
}

function DependencyRow({ dependency, tz, text, editing, onEdit, onText }: {
  dependency: MoveDependency
  tz: string
  text: string | null | undefined
  editing: boolean
  onEdit: () => void
  onText: (text: string | null) => void
}) {
  let title: string
  let sub: string | null
  let chip: ReactNode
  switch (dependency.kind) {
    case 'rental': {
      const named = MOVE_DAY.rentalTitle(dependency.description, dependency.vendor.shortName)
      title = dependency.confirmation ? MOVE_DAY.rentalTitle(named, dependency.confirmation) : named
      sub = MOVE_DAY.vendorAgree(dayText(dependency.from, 'day'), dayText(dependency.to, 'day'),
        dependency.vendor.shortName, dependency.vendor.phone ?? dependency.vendor.name)
      chip = <TagChip tone="warn">{MOVE_DAY.callVendor}</TagChip>
      break
    }
    case 'po':
      title = dependency.how === 'pickup'
        ? MOVE_DAY.pickupAt(dependency.vendor.name, dependency.po.number)
        : MOVE_DAY.rentalTitle(dependency.vendor.name, dependency.po.number)
      sub = MOVE_DAY.poMoves(timeIn(tz, dependency.from, 'dayClock'), timeIn(tz, dependency.to, 'dayClock'))
      chip = <TagChip tone="green">{MOVE_DAY.movesItself}</TagChip>
      break
    case 'equipment': {
      const day = dayText(dependency.on, 'weekday')
      const stay = dependency.staysOnSite.map((unit) => unit.tag)
      const repicked = [
        ...dependency.repicked.units.map((unit) => unit.tag),
        ...dependency.repicked.types.map((type) => MOVE_DAY.aType(type.noun)),
      ]
      title = MOVE_DAY.companyEquipment
      sub = stay.length > 0 && dependency.overWeekend && repicked.length > 0
        ? MOVE_DAY.equipmentMoves(COMMON.list(stay), COMMON.list(repicked), day)
        : stay.length > 0 && dependency.overWeekend
          ? MOVE_DAY.staysOnSite(COMMON.list(stay))
          : repicked.length > 0 ? MOVE_DAY.repicked(COMMON.list(repicked), day) : null
      chip = <TagChip tone="green">{MOVE_DAY.movesItself}</TagChip>
      break
    }
    case 'customer': {
      const shown = text === undefined ? dependency.text : text
      title = dependency.contact.name
      sub = shown ? MOVE_DAY.customerText(shown) : null
      chip = <TagChip tone="grey" onClick={onEdit} pressed={editing}>{MOVE_DAY.editText}</TagChip>
      break
    }
  }
  return (
    <div className="border-t border-line-faint py-[9px]">
      <div className="flex items-start gap-2.5">
        <span className="mt-px inline-flex text-text-muted"><VisitGlyph name={DEPENDENCY_GLYPHS[dependency.kind]} size={15} stroke={2} /></span>
        <div className="flex min-w-0 flex-1 flex-col">
          <b className="text-[12.5px]">{title}</b>
          {sub && <span className="text-[11.5px] leading-[1.4] text-text-muted">{sub}</span>}
        </div>
        {chip}
      </div>
      {dependency.kind === 'customer' && editing && (
        <textarea
          aria-label={MOVE_DAY.editText}
          value={text === undefined ? dependency.text : (text ?? '')}
          onChange={(event) => onText(event.target.value.trim() === '' ? null : event.target.value)}
          className="mt-2 block h-16 w-full resize-none tabular-nums rounded-button border border-line bg-surface px-3 py-2 text-[13px]
                     leading-normal text-text focus:border-accent-line focus:outline-none"
        />
      )}
    </div>
  )
}
